import api from '@/api/api';
import { toast } from '@/components/utils/ui_v4/alerts/toast/toast';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import {
  API_ENDPOINTS,
  DIAS_SEMANA_MAP,
  getStatusColor,
  getStatusIcon
} from '../constants';
import { extractDateAndTime, normalizeTime } from '../utils/dateUtils';
import { normalizeDayName } from '../utils/stringUtils';

export default function useNovaCotacaoForm(props, salvarRascunhoCallback = null) {
  const isLoading = ref(false);
  const isSavingRascunho = ref(false);
  const isPublicando = ref(false);

  const produtos = ref([]);
  const cotacao = ref(null);
  const empresa = ref(null);
  const pedidos = ref([]);
  const fornecedores = ref([]);
  const seguidores = ref([]);
  const locaisRecebimento = ref([]);
  const config = ref([]);

  const empresas = ref([]);
  const usuarios = ref([]);

  const dadosGerais = reactive({
    titulo: '',
    empresa: null,
    data_inicio: '',
    hora_inicio: '',
    data_fim: '',
    hora_fim: '',
    responsavel: null,
    seguidores: [],
    prazo_entrega_desejado_dias: 30,
    lembrete_diario: false,
    lembrete_diario_dias: 3,
  });

  const openLocalRecebimento = ref({
    isOpen: false,
    modo: 'create',
    itemEdit: null,
  });

  const isEmpresaDisabled = computed(() => {
    return empresas.value.length === 1;
  });

  const isDadosGeraisComplete = computed(() => {
    return !!(
      dadosGerais.titulo &&
      dadosGerais.empresa &&
      dadosGerais.data_inicio &&
      dadosGerais.hora_inicio &&
      dadosGerais.data_fim &&
      dadosGerais.hora_fim &&
      dadosGerais.responsavel
    );
  });

  const isLocaisRecebimentoComplete = computed(() => {
    return locaisRecebimento.value.length > 0;
  });

  const isProdutosComplete = computed(() => {
    return produtos.value.length > 0;
  });

  const isFornecedoresComplete = computed(() => {
    return fornecedores.value.length > 0;
  });

  const completedCount = computed(() => {
    let count = 0;
    if (isDadosGeraisComplete.value) count++;
    if (isLocaisRecebimentoComplete.value) count++;
    if (isProdutosComplete.value) count++;
    if (isFornecedoresComplete.value) count++;
    return count;
  });

  const totalRequired = 4;

  const progressPercent = computed(() => {
    return (completedCount.value / totalRequired) * 100;
  });

  const getDataFilial = async () => {
    try {
      const { data: dataEmpresas } = await api.get(API_ENDPOINTS.LISTAR_EMPRESAS);
      empresas.value = dataEmpresas.empresas_filiais || [];

      // Se houver apenas uma empresa, selecionar automaticamente
      if (empresas.value.length === 1) {
        dadosGerais.empresa = empresas.value[0].idfilial;
      }
    } catch (e) {
      void e;
    }
  };

  const getDataSeguidores = async () => {
    try {
      const { data: dataSeguidores } = await api.get(API_ENDPOINTS.LISTAR_SEGUIDORES);
      usuarios.value = dataSeguidores.seguidores || [];

      // Se não há responsável definido, selecionar o usuário logado
      if (!dadosGerais.responsavel) {
        const usuarioLogadoId = parseInt(localStorage.getItem('id'));
        if (usuarioLogadoId) {
          dadosGerais.responsavel = usuarioLogadoId;
        }
      }
    } catch (e) {
      void e;
    }
  };

  const getCotacao = async (fullUpdate = true) => {
    if (!props.idCotacao) {
      cotacao.value = null;
      return;
    }

    if (fullUpdate) {
      isLoading.value = true;
    }
    try {
      const { data: response } = await api.get(
        `${API_ENDPOINTS.BUSCAR_DETALHES}?cotacao_transacao_id=${props.idCotacao}`
      );

      produtos.value = response.produtos;
      cotacao.value = response.cotacao;
      empresa.value = response.empresa;
      pedidos.value = response.pedidos;
      fornecedores.value = response.fornecedores;
      seguidores.value = response.seguidores;
      config.value = response.config || [];

      if (fullUpdate) {
        // Normalizar locais de recebimento
        if (Array.isArray(response.locais_recebimento)) {
          locaisRecebimento.value = response.locais_recebimento.map((local) => {
            // Normalizar dias
            let diasNormalizados = [];
            if (Array.isArray(local.dias)) {
              diasNormalizados = local.dias.map((dia) => {
                if (typeof dia === 'object' && dia !== null) {
                  if (typeof dia.dia_semana_id !== 'undefined') {
                    const diaId = dia.dia_semana_id;
                    return DIAS_SEMANA_MAP[diaId] || diaId;
                  }
                  if (dia.nome) {
                    return normalizeDayName(dia.nome);
                  }
                }
                return dia;
              });
            }

            return {
              id: local.cotacao_local_recebimento_id || local.id,
              filial: local.idfilial || local.filial,
              endereco: local.endereco || '',
              inicio: normalizeTime(local.hora_recebimento_inicio || local.inicio),
              fim: normalizeTime(local.hora_recebimento_fim || local.fim),
              dias: diasNormalizados,
              observacao: local.observacao_recebimento || local.observacao || '',
              cotacao_local_recebimento_id: local.cotacao_local_recebimento_id,
              idfilial: local.idfilial,
              hora_recebimento_inicio: local.hora_recebimento_inicio,
              hora_recebimento_fim: local.hora_recebimento_fim,
              observacao_recebimento: local.observacao_recebimento,
            };
          });
        } else {
          locaisRecebimento.value = [];
        }

        // Preencher dados gerais
        if (cotacao.value && cotacao.value.cotacao_titulo) {
          dadosGerais.titulo = cotacao.value.cotacao_titulo;
        }

        const configPrimeiro = Array.isArray(response.config) && response.config.length > 0 ? response.config[0] : null;
        if (configPrimeiro && configPrimeiro.idfilial != null) {
          dadosGerais.empresa = configPrimeiro.idfilial;
        } else if (empresa.value && (empresa.value.idfilial != null || empresa.value.id != null)) {
          const empresaId = empresa.value.idfilial != null ? empresa.value.idfilial : empresa.value.id;
          dadosGerais.empresa = empresaId;
        } else if (empresas.value.length === 1) {
          dadosGerais.empresa = empresas.value[0].idfilial;
        }

        if (Array.isArray(seguidores.value) && seguidores.value.length > 0) {
          dadosGerais.seguidores = seguidores.value
            .map((seguidor) => seguidor.id)
            .filter((id) => id || id === 0);
        }

        if (response.responsavel) {
          dadosGerais.responsavel = response.responsavel.responsavel_id;
        } else {
          const usuarioLogadoId = parseInt(localStorage.getItem('id'));
          if (usuarioLogadoId && !dadosGerais.responsavel) {
            dadosGerais.responsavel = usuarioLogadoId;
          }
        }

        // Preencher período
        const hasConfigArray = (
          response && Array.isArray(response.config) && response.config.length > 0
        );
        const cfg = hasConfigArray ? response.config[0] : null;

        if (cfg) {
          const ini = extractDateAndTime(cfg.inicio_periodo_recebimento_proposta);
          const fim = extractDateAndTime(cfg.termino_periodo_recebimento_proposta);

          if (ini.date) dadosGerais.data_inicio = ini.date;
          if (ini.time) dadosGerais.hora_inicio = ini.time;
          if (fim.date) dadosGerais.data_fim = fim.date;
          if (fim.time) dadosGerais.hora_fim = fim.time;

        }

        if (cfg) {
          const diasLembrete = cfg.lembrete_diario_dias;
          const diasParsed = (diasLembrete !== null && diasLembrete !== undefined && diasLembrete !== '')
            ? Number(diasLembrete)
            : null;
          const diasValido = diasParsed !== null && Number.isFinite(diasParsed) && diasParsed >= 0;

          const cfgAtivo = cfg.lembrete_diario === true || cfg.lembrete_diario === 1 || cfg.lembrete_diario === '1';

          if (cfgAtivo) {
            dadosGerais.lembrete_diario = true;
            dadosGerais.lembrete_diario_dias = diasValido ? diasParsed : 3;
          } else if (diasValido && diasParsed === 0) {
            dadosGerais.lembrete_diario = true;
            dadosGerais.lembrete_diario_dias = 0;
          } else {
            dadosGerais.lembrete_diario = false;
            dadosGerais.lembrete_diario_dias = 3;
          }
        }

        const primeiroLocal = Array.isArray(response.locais_recebimento) && response.locais_recebimento.length > 0
          ? response.locais_recebimento[0]
          : null;
        const prazoLocal = primeiroLocal ? primeiroLocal.prazo_entrega_desejado_dias : undefined;
        if (prazoLocal !== null && prazoLocal !== undefined && prazoLocal !== '') {
          const parsed = Number(prazoLocal);
          dadosGerais.prazo_entrega_desejado_dias = Number.isFinite(parsed) ? parsed : 30;
        } else {
          dadosGerais.prazo_entrega_desejado_dias = 30;
        }
      }

    } catch (error) {

      toast.error('Não foi possivel encontrar cotação!');
    } finally {
      if (fullUpdate) {
        isLoading.value = false;
      }
    }
  };

  const handleAddLocal = () => {
    openLocalRecebimento.value = {
      isOpen: true,
      modo: 'create',
      itemEdit: null,
    };
  };

  const handleEditLocal = (item) => {
    openLocalRecebimento.value = {
      isOpen: true,
      modo: 'edit',
      itemEdit: {
        id: item.id,
        filial: item.filial,
        endereco: item.endereco,
        inicio: item.inicio,
        fim: item.fim,
        dias: item.dias,
        observacao: item.observacao,
      },
    };
  };

  const handleCloseLocalRecebimento = () => {
    openLocalRecebimento.value = {
      isOpen: false,
      modo: 'create',
      itemEdit: null,
    };
  };

  const handleSaveLocal = async (localData) => {
    if (openLocalRecebimento.value.modo === 'create') {
      locaisRecebimento.value.push(localData);
    } else {
      const index = locaisRecebimento.value.findIndex(l => l.id === localData.id);
      if (index !== -1) {
        locaisRecebimento.value[index] = localData;
      }
    }
    handleCloseLocalRecebimento();

    if (salvarRascunhoCallback) {
      await salvarRascunhoCallback();
    }
  };

  const handleDeleteLocal = (item) => {
    locaisRecebimento.value = locaisRecebimento.value.filter(l => l.id !== item.id);
  };

  const openVisualizarFornecedores = ref(false);
  const openVisualizarProdutos = ref(false);
  const openSeguidoresDialog = ref(false);
  const dialogVisible = ref(false);

  const handleVisualizar = (tipo) => {
    if (tipo === 'fornecedores') {
      openVisualizarFornecedores.value = true;
    } else if (tipo === 'produtos') {
      openVisualizarProdutos.value = true;
    }
  };

  const handleCloseVisualizarFornecedores = () => {
    openVisualizarFornecedores.value = false;
  };

  const refreshProdutos = async () => {
    if (!props.idCotacao) return true;

    try {
      const { data: response } = await api.get(
        `${API_ENDPOINTS.BUSCAR_DETALHES}?cotacao_transacao_id=${props.idCotacao}`
      );

      produtos.value = response.produtos;
      cotacao.value = response.cotacao;
      pedidos.value = response.pedidos;
      fornecedores.value = response.fornecedores;

      return true;
    } catch (error) {

      if (error.response && (error.response.status === 404 || error.response.status === 400)) {
        return false;
      }
      return true;
    }
  };

  const handleCloseVisualizarProdutos = async () => {
    openVisualizarProdutos.value = false;
    // Recarregar produtos após fechar o dialog
    const cotacaoExiste = await refreshProdutos();

    if (!cotacaoExiste && props.handleClose) {
      props.handleClose();
    }
  };

  const visualizarSeguidores = () => {
    openSeguidoresDialog.value = true;
  };

  const handleCloseSeguidores = () => {
    openSeguidoresDialog.value = false;
  };

  const handleSaveSeguidores = (seguidoresSelecionados) => {
    dadosGerais.seguidores = seguidoresSelecionados;
    handleCloseSeguidores();
  };

  const getNomeFilial = (idfilial) => {
    const filial = empresas.value.find(e => e.idfilial === idfilial);
    return filial ? (filial.razao_social || filial.nome_fantasia) : '';
  };

  watch(() => props.visible, (newValue) => {
    dialogVisible.value = newValue;
  }, { immediate: true });

  onMounted(async () => {
    await getDataFilial();
    await getDataSeguidores();
  });

  watch(() => props.visible, async (newVisible) => {
    if (newVisible) {
      await getCotacao();
    }
  });

  const setFornecedores = (novosFornecedores) => {
    if (Array.isArray(novosFornecedores)) {
      fornecedores.value = novosFornecedores;
    }
  };

  return {
    isLoading,
    isSavingRascunho,
    isPublicando,
    produtos,
    cotacao,
    empresa,
    pedidos,
    fornecedores,
    setFornecedores,
    seguidores,
    locaisRecebimento,
    config,
    empresas,
    usuarios,
    dadosGerais,
    isEmpresaDisabled,
    isDadosGeraisComplete,
    isLocaisRecebimentoComplete,
    isProdutosComplete,
    isFornecedoresComplete,
    completedCount,
    totalRequired,
    progressPercent,
    getCotacao,
    getDataFilial,
    getDataSeguidores,
    openLocalRecebimento,
    handleAddLocal,
    handleEditLocal,
    handleCloseLocalRecebimento,
    handleSaveLocal,
    handleDeleteLocal,
    openVisualizarFornecedores,
    openVisualizarProdutos,
    openSeguidoresDialog,
    dialogVisible,
    handleVisualizar,
    handleCloseVisualizarFornecedores,
    handleCloseVisualizarProdutos,
    visualizarSeguidores,
    handleCloseSeguidores,
    handleSaveSeguidores,
    getNomeFilial,
    getStatusColor,
    getStatusIcon,
  };
}
